// src/pages/Cart.js
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import {
  ShoppingCart, Trash2, Minus, Plus, ArrowLeft,
  ShoppingBag, Truck, ShieldCheck, Tag
} from 'lucide-react';

const Cart = () => {
  const navigate = useNavigate();
  const {
    cartItems,
    removeFromCart,
    incrementQuantity,
    decrementQuantity,
    clearCart,
    getSubtotal,
    getTotalSavings,
    getItemCount,
  } = useCart();

  const subtotal = getSubtotal();
  const savings = getTotalSavings();
  const itemCount = getItemCount();

  const shippingFee = subtotal >= 2000 ? 0 : (subtotal > 0 ? 200 : 0);
  const total = subtotal + shippingFee;
  const freeShippingRemaining = subtotal < 2000 ? 2000 - subtotal : 0;

  if (cartItems.length === 0) {
    return (
      <div className="min-h-screen py-12 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div
            className="rounded-2xl p-12 anim-scaleIn"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
          >
            <ShoppingBag size={80} className="mx-auto mb-4 anim-float" style={{ color: 'var(--color-secondary-text)' }} />
            <h2 className="text-2xl font-bold mb-6" style={{ color: 'var(--color-primary-text)' }}>
              Your Cart is Empty
            </h2>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold btn-2d shadow-lg"
              style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
            >
              <ShoppingCart size={18} /> Start Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-6 anim-fadeIn" style={{ backgroundColor: 'var(--color-body-bg)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-6">

        {/* Header */}
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-full btn-2d"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold" style={{ color: 'var(--color-primary-text)' }}>
                Shopping Cart
              </h1>
              <p className="text-sm" style={{ color: 'var(--color-secondary-text)' }}>
                {itemCount} {itemCount === 1 ? 'item' : 'items'} in cart
              </p>
            </div>
          </div>
          <button
            onClick={() => { if (window.confirm('Cart clear karein?')) clearCart(); }}
            className="text-sm font-semibold text-red-500 hover:text-red-600 flex items-center gap-1 btn-2d px-3 py-2 rounded-lg"
          >
            <Trash2 size={14} /> Clear Cart
          </button>
        </div>

        {/* Free Shipping Progress */}
        {freeShippingRemaining > 0 && (
          <div
            className="rounded-xl p-4 mb-4 anim-fadeInUp"
            style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
          >
            <div className="flex items-center gap-2 mb-2">
              <Truck size={18} style={{ color: 'var(--color-link)' }} />
              <p className="text-sm font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                Rs. {freeShippingRemaining} aur add karein → <span style={{ color: 'var(--color-link)' }}>Free Shipping!</span>
              </p>
            </div>
            <div className="h-2 rounded-full overflow-hidden" style={{ backgroundColor: 'var(--color-border)' }}>
              <div
                className="h-full transition-all duration-500"
                style={{
                  width: `${Math.min((subtotal / 2000) * 100, 100)}%`,
                  background: 'linear-gradient(90deg, #16a34a, #facc15)',
                }}
              />
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* CART ITEMS */}
          <div className="lg:col-span-2 space-y-3">
            {cartItems.map((item, i) => (
              <div
                key={item.id}
                className="rounded-xl p-4 card-lift anim-fadeInUp flex gap-4"
                style={{
                  backgroundColor: 'var(--color-card-bg)',
                  border: `1px solid var(--color-border)`,
                  animationDelay: `${i * 100}ms`,
                }}
              >
                <Link to={`/product/${item.id}`} className="shrink-0">
                  <img
                    src={item.image || 'https://via.placeholder.com/100'}
                    alt={item.title}
                    className="w-20 h-20 md:w-24 md:h-24 object-cover rounded-lg"
                  />
                </Link>

                <div className="flex-1 min-w-0">
                  <Link to={`/product/${item.id}`}>
                    <h3 className="font-bold text-sm md:text-base line-clamp-2 hover:opacity-80"
                      style={{ color: 'var(--color-primary-text)' }}>
                      {item.title}
                    </h3>
                  </Link>
                  <p className="text-xs mt-1" style={{ color: 'var(--color-secondary-text)' }}>
                    {item.category}
                  </p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="font-bold text-sm" style={{ color: 'var(--color-link)' }}>
                      Rs. {item.price}
                    </span>
                    {item.originalPrice > item.price && (
                      <span className="text-xs line-through" style={{ color: 'var(--color-secondary-text)' }}>
                        Rs. {item.originalPrice}
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                    <div
                      className="flex items-center rounded-full"
                      style={{ border: `1px solid var(--color-border)` }}
                    >
                      <button
                        onClick={() => decrementQuantity(item.id)}
                        className="p-1.5 md:p-2 rounded-l-full btn-2d"
                        style={{ color: 'var(--color-primary-text)' }}
                      >
                        <Minus size={14} />
                      </button>
                      <span className="px-3 font-bold text-sm min-w-[30px] text-center"
                        style={{ color: 'var(--color-primary-text)' }}>
                        {item.quantity}
                      </span>
                      <button
                        onClick={() => incrementQuantity(item.id)}
                        className="p-1.5 md:p-2 rounded-r-full btn-2d"
                        style={{ color: 'var(--color-primary-text)' }}
                      >
                        <Plus size={14} />
                      </button>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold hidden sm:inline"
                        style={{ color: 'var(--color-primary-text)' }}>
                        Rs. {item.price * item.quantity}
                      </span>
                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="p-2 rounded-lg hover:bg-red-50 text-red-500 transition btn-2d"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* ORDER SUMMARY */}
          <div className="lg:col-span-1">
            <div
              className="rounded-2xl p-5 sticky top-24 anim-fadeInRight"
              style={{ backgroundColor: 'var(--color-card-bg)', border: `1px solid var(--color-border)` }}
            >
              <h2 className="text-lg font-bold mb-4" style={{ color: 'var(--color-primary-text)' }}>
                Order Summary
              </h2>

              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-secondary-text)' }}>Subtotal</span>
                  <span className="font-semibold" style={{ color: 'var(--color-primary-text)' }}>
                    Rs. {subtotal}
                  </span>
                </div>

                {savings > 0 && (
                  <div className="flex justify-between">
                    <span className="flex items-center gap-1" style={{ color: 'var(--color-link)' }}>
                      <Tag size={14} /> Savings
                    </span>
                    <span className="font-semibold" style={{ color: 'var(--color-link)' }}>
                      - Rs. {savings}
                    </span>
                  </div>
                )}

                <div className="flex justify-between">
                  <span style={{ color: 'var(--color-secondary-text)' }}>Shipping</span>
                  <span className="font-semibold" style={{ color: shippingFee === 0 ? 'var(--color-link)' : 'var(--color-primary-text)' }}>
                    {shippingFee === 0 ? 'FREE' : `Rs. ${shippingFee}`}
                  </span>
                </div>

                <div className="border-t pt-3 mt-3 flex justify-between text-lg"
                  style={{ borderColor: 'var(--color-border)' }}>
                  <span className="font-bold" style={{ color: 'var(--color-primary-text)' }}>Total</span>
                  <span className="font-bold" style={{ color: 'var(--color-link)' }}>
                    Rs. {total}
                  </span>
                </div>
              </div>

              <button
                onClick={() => navigate('/checkout')}
                className="w-full mt-5 rounded-full py-4 font-bold flex items-center justify-center gap-2 btn-2d shadow-xl ripple-btn"
                style={{ backgroundColor: 'var(--color-button-bg)', color: 'var(--color-button-text)' }}
              >
                <ShieldCheck size={20} /> Proceed to Checkout
              </button>

              <Link
                to="/"
                className="w-full mt-3 rounded-full py-3 font-semibold flex items-center justify-center gap-2 btn-2d"
                style={{ border: `1px solid var(--color-border)`, color: 'var(--color-primary-text)' }}
              >
                Continue Shopping
              </Link>

              <div className="flex items-center justify-center gap-2 mt-4 text-xs"
                style={{ color: 'var(--color-secondary-text)' }}>
                <ShieldCheck size={14} /> 100% Secure Checkout
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default Cart;